import { LitElement, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Task } from "@lit/task";
import { fetchApi, types, imageCache } from "../api/index.js";
import { relativeTime, apiLink } from "../utils/format.js";
import { navigate } from "../router.js";
import "../components/loading-spinner.js";
import "../components/error-message.js";

@customElement("directory-page")
export class DirectoryPage extends LitElement {
  @state() private _search = "";
  @state() private _category = "";
  @state() private _view: "grid" | "list" = "grid";

  private _onNavigateHome = () => { this._search = ""; this._category = ""; };

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("app-navigate-home", this._onNavigateHome);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("app-navigate-home", this._onNavigateHome);
  }

  createRenderRoot() {
    return this;
  }

  private _imagesTask = new Task(this, {
    task: async () => {
      const data = await fetchApi<types.DirectoryResponse>("/v1/images");
      imageCache.storeImages(data.images);
      return data;
    },
    args: () => [] as const,
  });

  private _filteredImages(images: types.ImageSummary[]): types.ImageSummary[] {
    let result = images;
    if (this._category) {
      result = result.filter((img) => img.application_category === this._category);
    }
    if (this._search) {
      const q = this._search.toLowerCase();
      result = result.filter((img) => img.name.toLowerCase().includes(q) || img.description.toLowerCase().includes(q) || img.summary.toLowerCase().includes(q));
    }
    return result;
  }

  private _onSearch(e: Event) {
    this._search = (e.target as HTMLInputElement).value;
  }

  private _onCategory(category: string) {
    this._category = this._category === category ? "" : category;
  }

  private _clickImage(name: string, e: Event) {
    e.preventDefault();
    navigate(`/image/${name}`);
  }

  private _renderDeprecatedBadge(streams: string[] | undefined) {
    if (!streams?.length) return nothing;
    return html`
      <span
        class="px-1.5 py-0.5 text-xs rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 whitespace-nowrap"
        title=${`Deprecated streams: ${streams.join(", ")}`}
      >deprecated</span>
    `;
  }

  private _renderCategoryFilter(images: types.ImageSummary[]) {
    const categories = [...new Set(images.map((img) => img.application_category))].sort();
    if (categories.length <= 1) return nothing;
    const btn = (label: string, active: boolean, onClick: () => void) => html`
      <button
        @click=${onClick}
        class="px-2 py-1 text-xs cursor-pointer transition-colors border -ml-px first:ml-0
               rounded-none first:rounded-l last:rounded-r
               ${active ? "bg-hb-accent text-hb-bg border-hb-accent relative z-10" : "bg-hb-surface text-gray-400 hover:text-gray-200 border-hb-border"}"
      >${label}</button>
    `;
    return html`
      <div class="flex flex-wrap mb-4">
        ${btn("All", !this._category, () => (this._category = ""))}
        ${categories.map((c) => btn(c, this._category === c, () => this._onCategory(c)))}
      </div>
    `;
  }

  private _renderStats() {
    return this._imagesTask.render({
      pending: () => html`<span class="text-gray-500 text-sm">Loading stats...</span>`,
      error: () => nothing,
      complete: (data) => html`
        <span class="text-gray-400 text-sm">
          ${data.image_count.toLocaleString()} images &middot; ${data.stream_count.toLocaleString()} streams &middot; ${data.release_count.toLocaleString()} releases
          ${apiLink("/v1/images")}
        </span>
      `,
    });
  }

  private _renderImageCard(img: types.ImageSummary) {
    return html`
      <a
        href="/image/${img.name}"
        @click=${(e: Event) => this._clickImage(img.name, e)}
        class="block bg-hb-surface border border-hb-border rounded-lg p-4 hover:border-hb-accent transition-colors"
      >
        <div class="flex items-baseline justify-between gap-2">
          <h3 class="text-white font-bold text-base">${img.name}</h3>
          <span class="text-right min-w-0">
            <span class="text-gray-500 text-xs">${relativeTime(img.oldest_created)}</span>
            <span class="text-hb-accent text-sm ml-1" title=${img.latest_tag}>${img.latest_tag}</span>
          </span>
        </div>
        <p class="text-gray-400 text-sm mt-1 line-clamp-3">${img.description}</p>
        <div class="mt-3 flex flex-wrap items-center gap-1">
          <span class="px-1.5 py-0.5 text-xs rounded bg-hb-bg text-emerald-600 dark:text-emerald-400/70 border border-emerald-600/30 dark:border-emerald-400/30 whitespace-nowrap">${img.application_category}</span>
          ${img.streams.length > 1
            ? img.streams.map(
                (s) => html`<span class="px-1.5 py-0.5 text-xs rounded bg-hb-bg text-blue-400/70 border border-blue-400/30 whitespace-nowrap">${s}</span>`,
              )
            : ""}
          ${this._renderDeprecatedBadge(img.deprecated_streams)}
          ${(() => { const bases = [...new Set(img.variants.map((v) => v.base))].sort((a, b) => a === "default" ? -1 : b === "default" ? 1 : a.localeCompare(b)); return bases.length > 1 ? bases : bases.filter((b) => b !== "default"); })().map(
            (base) => html`<span class="px-1.5 py-0.5 text-xs rounded bg-hb-bg text-hb-accent/70 border border-hb-accent/30 whitespace-nowrap">${base}</span>`,
          )}
          ${img.architectures.map(
            (a) => html`<span class="px-1.5 py-0.5 text-xs rounded bg-hb-bg text-gray-400 border border-hb-border whitespace-nowrap">${a}</span>`,
          )}
          ${img.variants.some((v) => v.builder) || img.variants.some((v) => v.fips)
            ? html`<span class="ml-auto flex gap-1">
                ${img.variants.some((v) => v.builder)
                  ? html`<span class="px-1.5 py-0.5 text-xs rounded bg-purple-500/30 text-purple-200 whitespace-nowrap">builder</span>`
                  : ""}
                ${img.variants.some((v) => v.fips)
                  ? html`<span class="px-1.5 py-0.5 text-xs rounded bg-yellow-500/30 text-yellow-200 whitespace-nowrap">fips</span>`
                  : ""}
              </span>`
            : ""}
        </div>
      </a>
    `;
  }

  private _renderImageRow(img: types.ImageSummary) {
    return html`
      <tr
        @click=${(e: Event) => this._clickImage(img.name, e)}
        class="border-b border-hb-border hover:bg-hb-surface/50 cursor-pointer transition-colors"
      >
        <td class="py-2 px-3 text-white font-bold text-sm">
          <div class="flex flex-wrap items-center gap-1">
            <span>${img.name}</span>
            ${this._renderDeprecatedBadge(img.deprecated_streams)}
          </div>
        </td>
        <td class="py-2 px-3 text-gray-400 text-sm hidden md:table-cell">${img.application_category}</td>
        <td class="py-2 px-3 text-gray-400 text-sm hidden md:table-cell">${img.summary || img.description}</td>
        <td class="py-2 px-3 text-hb-accent text-sm">${img.latest_tag}</td>
        <td class="py-2 px-3 text-gray-500 text-xs hidden lg:table-cell">${img.architectures.join(", ")}</td>
        <td class="py-2 px-3 text-gray-500 text-xs">${relativeTime(img.oldest_created)}</td>
      </tr>
    `;
  }

  render() {
    return html`
      <div>
        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 class="text-white text-2xl font-bold">Container Images</h1>
            <div class="mt-1">${this._renderStats()}</div>
          </div>
          <div class="flex items-center gap-2">
            <input
              type="text"
              placeholder="Search images..."
              .value=${this._search}
              @input=${this._onSearch}
              class="bg-hb-surface border border-hb-border rounded-lg px-3 py-1.5 text-sm text-gray-200
                     placeholder:text-gray-500 focus:outline-none focus:border-hb-accent w-48"
            />
            <div class="flex border border-hb-border rounded-lg overflow-hidden">
              <button
                @click=${() => (this._view = "grid")}
                class="px-2 py-1.5 text-xs cursor-pointer ${this._view === "grid" ? "bg-hb-accent text-hb-bg" : "bg-hb-surface text-gray-400"}"
              >
                Grid
              </button>
              <button
                @click=${() => (this._view = "list")}
                class="px-2 py-1.5 text-xs cursor-pointer ${this._view === "list" ? "bg-hb-accent text-hb-bg" : "bg-hb-surface text-gray-400"}"
              >
                List
              </button>
            </div>
          </div>
        </div>

        ${this._imagesTask.render({
          pending: () => html`<loading-spinner></loading-spinner>`,
          error: (e) => html`<error-message .message=${String(e)}></error-message>`,
          complete: (data) => {
            const images = this._filteredImages(data.images);
            const categoryFilter = this._renderCategoryFilter(data.images);
            if (images.length === 0) {
              return html`${categoryFilter}<p class="text-gray-500 text-sm py-8 text-center">No images match your search.</p>`;
            }
            if (this._view === "grid") {
              return html`
                ${categoryFilter}
                <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  ${images.map((img) => this._renderImageCard(img))}
                </div>
              `;
            }
            return html`
              ${categoryFilter}
              <div class="border border-hb-border rounded-lg overflow-hidden">
                <table class="w-full text-left">
                  <thead class="bg-hb-surface text-gray-400 text-xs">
                    <tr>
                      <th class="py-2 px-3">Name</th>
                      <th class="py-2 px-3 hidden md:table-cell">Category</th>
                      <th class="py-2 px-3 hidden md:table-cell">Description</th>
                      <th class="py-2 px-3">Latest</th>
                      <th class="py-2 px-3 hidden lg:table-cell">Architectures</th>
                      <th class="py-2 px-3">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${images.map((img) => this._renderImageRow(img))}
                  </tbody>
                </table>
              </div>
            `;
          },
        })}
      </div>
    `;
  }
}
